/**
 * Mood Statistic Chart Module
 *
 * Initializes Chart.js mood statistic visualization
 */

export const initMoodChart = () => {
	const chartElement = document.getElementById('moodProgressChart');

	if (!chartElement || typeof Chart === 'undefined') return;

	// Parse data from element attributes
	const stagesData = JSON.parse(chartElement.getAttribute('data-stages') || '[]');
	const evaluationsData = JSON.parse(chartElement.getAttribute('data-evaluations') || '[]');

	if (stagesData.length === 0) return;

	// 1. Calculate the maximum number of evaluations in any stage to define a uniform width
	const actualMaxEvals = stagesData.reduce((max, stage) => {
		const count = evaluationsData.filter(e => e.stage_id == stage.id).length;
		return Math.max(max, count);
	}, 0);

	const slotsPerStage = Math.max(4, actualMaxEvals);
	const totalSlots = stagesData.length * slotsPerStage;

	const dataLabels = Array.from({ length: totalSlots }, () => '');
	const moodValues = Array.from({ length: totalSlots }, () => null);
	const evalMeta = Array.from({ length: totalSlots }, () => null);
	const stageInfos = [];

	stagesData.forEach((stage, sIdx) => {
		const startIdx = sIdx * slotsPerStage;
		const endIdx = startIdx + slotsPerStage - 1;
		const centerIdx = (startIdx + endIdx) / 2;

		stageInfos.push({
			id: stage.id,
			name: stage.label,
			status: stage.status,
			start: startIdx,
			end: endIdx,
			center: centerIdx
		});

		// Fill evaluations for this stage
		const stageEvals = evaluationsData.filter(e => e.stage_id == stage.id);
		stageEvals.forEach((ev, eIdx) => {
			if (eIdx < slotsPerStage) { // Safety check
				dataLabels[startIdx + eIdx] = ev.date || '';
				moodValues[startIdx + eIdx] = ev.point;
				evalMeta[startIdx + eIdx] = {
					title: ev.title,
					description: ev.feel
				};
			}
		});
	});

	// Custom plugin to draw stage background highlight (behind) and labels (above)
	const stageDecoratorPlugin = {
		id: 'stageDecoratorPlugin',
		beforeDraw: (chart) => {
			const { ctx, chartArea: { top, bottom }, scales: { x } } = chart;

			stageInfos.forEach((info, index) => {
				// Calculate visual boundaries using midpoints
				// Start: 0 for Stage 1, otherwise midpoint between stages
				let drawStart = info.start;
				if (index > 0) {
					drawStart = (stageInfos[index - 1].end + info.start) / 2;
				}

				// End: end of the last stage, otherwise midpoint to the next one
				let drawEnd = info.end;
				if (index < stageInfos.length - 1) {
					drawEnd = (info.end + stageInfos[index + 1].start) / 2;
				}

				const xLeft = x.getPixelForValue(drawStart);
				const xRight = x.getPixelForValue(drawEnd);

				// Save calculated positions for afterDraw
				info.xLeft = xLeft;
				info.xRight = xRight;

				// 1. Highlight "In Progress" background
				if (info.status === 'In Progress') {
					ctx.save();
					ctx.fillStyle = '#FDF9F1';
					ctx.fillRect(xLeft, top, xRight - xLeft, bottom - top);
					ctx.restore();
				}

				// 2. Draw vertical dotted lines between stages
				if (index < stageInfos.length - 1) {
					ctx.save();
					ctx.setLineDash([4, 4]);
					ctx.strokeStyle = '#D1D5DB';
					ctx.lineWidth = 1;
					ctx.beginPath();
					ctx.moveTo(xRight, top);
					ctx.lineTo(xRight, bottom);
					ctx.stroke();
					ctx.restore();
				}
			});
		},
		afterDraw: (chart) => {
			const { ctx, chartArea: { bottom }, scales: { x } } = chart;

			stageInfos.forEach((info) => {
				const xCenter = (info.xLeft + info.xRight) / 2;

				// 2. Draw Stage Label centered in its visual territory
				ctx.save();
				ctx.fillStyle = '#36383A';
				ctx.font = '400 12px Montserrat';
				ctx.textAlign = 'center';
				ctx.textBaseline = 'top';
				ctx.fillText(info.name, xCenter, bottom + 15);
				ctx.restore();

				// 3. Draw a small vertical tick at the center of the territory
				ctx.save();
				ctx.strokeStyle = '#D1D5DB';
				ctx.lineWidth = 1;
				ctx.beginPath();
				ctx.moveTo(xCenter, bottom);
				ctx.lineTo(xCenter, bottom + 8);
				ctx.stroke();
				ctx.restore();
			});
		}
	};

	// eslint-disable-next-line no-undef
	new Chart(chartElement, {
		type: 'line',
		plugins: [stageDecoratorPlugin],
		data: {
			labels: dataLabels,
			datasets: [
				{
					label: 'Mood Statistic',
					data: moodValues,
					borderColor: '#B5B7BA',
					borderDash: [4, 4],
					tension: 0,
					fill: false,
					pointRadius: 3,
					pointHoverRadius: 6,
					pointBackgroundColor: '#B5B7BA',
					pointBorderWidth: 4,
					borderWidth: 1,
					spanGaps: true,
					clip: false
				}
			]
		},
		options: {
			responsive: true,
			maintainAspectRatio: false,
			layout: {
				padding: {
					left: 10,
					right: 10,
					bottom: 40
				}
			},
			plugins: {
				legend: { display: false },
				tooltip: {
					displayColors: false,
					callbacks: {
						title: (items) => {
							const idx = items[0].dataIndex;
							const meta = evalMeta[idx];
							const point = moodValues[idx];
							return meta ? `${point} points` : '';
						},
						label: (item) => {
							const idx = item.dataIndex;
							const meta = evalMeta[idx];
							return meta ? meta.description : '';
						}
					}
				}
			},
			scales: {
				y: {
					min: 1,
					max: 10,
					border: {
						dash: [3, 3],
						color: '#D1D5DB'
					},
					grid: {
						color: '#F3F4F6',
						lineWidth: 1,
					},
					ticks: {
						stepSize: 1,
						color: '#6B7280',
						font: {
							family: 'Montserrat',
							size: 12,
							weight: '400'
						}
					}
				},
				x: {
					offset: false,
					border: {
						dash: [4, 4],
						color: '#D1D5DB'
					},
					grid: {
						display: false
					},
					ticks: {
						display: false
					}
				}
			}
		}
	});
};
